import React, {useEffect, useRef, useState} from 'react';
import {
  I18nManager,
  Keyboard,
  Modal,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Picker} from '@react-native-picker/picker';
import {Theme} from '../../theme/lightTheme';
import MyPressable from '../myPressable';
import {tr} from '../../translations';
import TextGeneric from '../textGeneric';
import {
  GenericPickerProps,
  PickerItem,
} from '../../interfaces/components/picker';
import TextInput from '../textInput';
import {hdp} from '../../utils/responsive';

/**
 * Outer container for the picker component.
 */
const Container = styled.View`
  width: 100%;
`;

/**
 * Modal container for iOS platform.
 */
const ModalContainer = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 100%;
`;

/**
 * Main modal container for iOS platform.
 */
const MainModalContainer = styled.View`
  position: absolute;
  bottom: 0;
  padding-bottom: ${hdp(4)}px;
  width: 100%;
  left: 0;
  padding-horizontal: 2%;
`;

/**
 * Bottom container for iOS platform with cancel and done buttons.
 */
const BottomContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  background-color: ${({theme}) => theme.text.light};
  border-radius: 8px;
`;

/**
 * Picker component for iOS platform.
 */
const PickerContainer = styled(Picker)`
  border-radius: 8px;
  margin-bottom: 4%;
  background-color: ${({theme}) => theme.text.light};
`;

/**
 * Action button (Cancel and Done) for iOS platform.
 */
const ActionButton = styled(MyPressable)`
  min-width: 50%;
  min-height: ${hdp(5.5)}px;
  padding-vertical: 2%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

/**
 * Text component for "Cancel" button on iOS platform.
 */
const CancelText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.black + '77'};
`;

/**
 * Text component for "Done" button on iOS platform.
 */
const DoneText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.primary};
`;

/**
 * Container for Android platform.
 */
const AndroidContainer = styled.View<{isTouching?: boolean}>`
  height: 40px;
  opacity: ${({isTouching}) => (isTouching ? 0.7 : 1)};
`;

/**
 * Picker component for Android platform.
 */
const AndroidPicker = styled(Picker)`
  width: 100%;
  font-weight: bold;
  opacity: 0;
  z-index: 1;
`;

/**
 * Pressable container for Android platform to capture touch events.
 */
const AndroidPressableContainer = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  pointer-events: none;
`;

/**
 * GenericPicker component to select items from a list.
 *
 * @param isRequired
 * @param customComponent
 * @param isRequired
 * @param customComponent
 * @param isUnselectAllowed
 * @param {GenericPickerProps} props - Component props.
 * @param {React.CSSProperties} [props.style] - Style for the outer container.
 * @param {(value: string | number) => void} [props.onValueChange] - Callback when a value is selected.
 * @param {PickerItem[]} props.list - List of items to choose from.
 * @param {string} props.labelKey - Key for the label property in the list item.
 * @param {string} props.valueKey - Key for the value property in the list item.
 * @param {string} props.label - Label for the picker component.
 * @param {string | number} props.value - Selected value for the picker component.
 * @param {boolean} props.disabled - Key for disabling the component.
 * @returns {React.ReactElement} - JSX element representing the GenericPicker component.
 */
const GenericPicker = ({
  style,
  onValueChange,
  list,
  labelKey,
  valueKey,
  label,
  value = '',
  disabled = false,
  isRequired,
  customComponent = null,
  isUnselectAllowed,
  containerStyle = {},
  ...props
}: GenericPickerProps): React.ReactElement => {
  /**
   * The current theme used in the component.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  /**
   * State variable to manage the visibility of the modal.
   * @type {[boolean, SetModalVisible]}
   */
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  /**
   * State variable to store the temporary selected value in the modal.
   * @type {[string | number, SetTempValue]}
   */
  const [tempValue, setTempValue] = useState<string>(value || '');
  /**
   * State variable to store the current selected value in the modal.
   * @type {[string | number, SetCurrentValue]}
   */
  const [currentValue, setCurrentValue] = useState<string>(value || '');
  /**
   * Reference to the Picker component used for Android platform.
   * @type {PickerRef}
   */
  const pickerRef = useRef<Picker<any>>(null);
  /**
   * State variable to manage the enabled state of the Picker on Android platform.
   * @type {[boolean, SetEnabled]}
   */
  const [enabled, setEnabled] = useState<boolean>(false);
  const [isTouching, setIsTouching] = useState<boolean>(false);
  const [androidHeight, setAndroidHeight] = useState(40); // Initial height

  const handleLayout = (event: any) => {
    const {height} = event.nativeEvent.layout;
    setAndroidHeight(height);
  };
  /**
   * Press Cancel button handler.
   *
   * @returns {void}
   */
  const pressCancel = (): void => {
    setTempValue(currentValue);
    setModalVisible(false);
  };
  useEffect(() => {
    setCurrentValue(value || '');
    setTempValue(value || '');
  }, [value]);

  /**
   * Press Done button handler.
   *
   * @returns {void}
   */
  const pressDone = (): void => {
    if ((tempValue || '').trim().length === 0) {
      setCurrentValue('');
      onValueChange && onValueChange('');
      setModalVisible(false);
      return;
    }
    // Set State
    setCurrentValue(tempValue);
    // Props: onChange
    onValueChange && onValueChange(tempValue);
    // Toggle Modal
    setModalVisible(false);
  };

  /**
   * Select a value based on the platform (iOS or Android).
   *
   * @param {string} currentValue - The selected value.
   * @returns {void}
   */
  const selectValue = (currentValue: string) => {
    // Platform: iOS
    if (Platform.OS === 'ios') {
      // Set State
      setTempValue(currentValue);
    }
    // Platform: Android
    else {
      // Set State
      setCurrentValue(currentValue);
      if (onValueChange) {
        onValueChange(currentValue);
      }
    }
  };

  /**
   * Get the label to display in the TextInput based on the selected value.
   *
   * @returns {string} The label to display.
   */
  const getLabel = (): string => {
    if (labelKey && valueKey) {
      const result = (list as PickerItem[]).find(
        (item: PickerItem) => item[valueKey] === currentValue,
      );
      if (result) {
        return (result[labelKey] || '')?.toString();
      }
    }
    return currentValue?.toString();
  };

  const showIOSModal = () => {
    Keyboard.dismiss();
    setModalVisible(true);
  };

  /**
   * Render the iOS picker component.
   *
   * @returns {React.ReactElement} JSX element representing the iOS picker component.
   */
  const renderIOS = () => {
    return (
      <Container style={style}>
        <TouchableOpacity
          activeOpacity={0.7}
          style={[
            style,
            disabled && {
              opacity: 0.5,
            },
          ]}
          disabled={disabled}
          onPress={showIOSModal}>
          {customComponent ? (
            customComponent()
          ) : (
            <TextInput
              label={label}
              value={getLabel()}
              editable={false}
              onChangeText={onValueChange}
              showRightIcon
              rightIconName={'arrowDown'}
              isRequired={isRequired}
              style={[{pointerEvents: 'none'}, style]}
              containerStyle={containerStyle}
              {...props}
            />
          )}
        </TouchableOpacity>
        <Modal visible={modalVisible} animationType={'slide'} transparent>
          <ModalContainer>
            <MainModalContainer>
              <PickerContainer
                selectedValue={
                  tempValue !== undefined ? tempValue : currentValue
                }
                onValueChange={(currentValue: any) =>
                  selectValue(currentValue)
                }>
                <Picker.Item
                  fontFamily={
                    I18nManager.isRTL
                      ? theme.fonts.ar.semi_bold
                      : theme.fonts.en.semi_bold
                  }
                  style={{fontWeight: 'bold'}}
                  label={
                    currentValue && isUnselectAllowed
                      ? tr('picker.unSelect')
                      : tr('picker.select')
                  }
                  value={currentValue && isUnselectAllowed ? ' ' : ''}
                  enabled={!!currentValue && isUnselectAllowed}
                />
                {list.map((item: string | PickerItem | any, i: number) => {
                  return (
                    <Picker.Item
                      key={i}
                      label={
                        labelKey && typeof item !== 'string'
                          ? item[labelKey]
                          : item
                      }
                      value={
                        valueKey && typeof item !== 'string'
                          ? item[valueKey]
                          : item
                      }
                    />
                  );
                })}
              </PickerContainer>
              <BottomContainer>
                <ActionButton borderRadius={4} onPress={() => pressCancel()}>
                  <CancelText bold>{tr('app.cancel')}</CancelText>
                </ActionButton>
                <ActionButton
                  borderRadius={4}
                  onPress={() => pressDone()}
                  containerStyle={
                    (currentValue === tempValue || !tempValue) && {
                      opacity: 0.4,
                    }
                  }
                  disabled={currentValue === tempValue || !tempValue}>
                  <DoneText bold>{tr('app.ok')}</DoneText>
                </ActionButton>
              </BottomContainer>
            </MainModalContainer>
          </ModalContainer>
        </Modal>
      </Container>
    );
  };

  /**
   * Render the Android picker component.
   *
   * @returns {React.ReactElement} JSX element representing the Android picker component.
   */
  const renderAndroid = () => {
    return (
      <AndroidContainer
        onStartShouldSetResponder={() => true}
        onResponderGrant={() => {
          setIsTouching(true);
        }}
        onResponderRelease={() => setIsTouching(false)}
        isTouching={isTouching}
        style={[style, {height: androidHeight}, containerStyle]}>
        <AndroidPicker
          style={[style, {height: androidHeight}, containerStyle]}
          ref={pickerRef}
          mode="dropdown"
          selectedValue={value?.toString()}
          itemStyle={{fontWeight: 'bold'}}
          dropdownIconColor={'white'}
          onFocus={() => {
            Keyboard.dismiss();
            setEnabled(false);
          }}
          onBlur={() => setEnabled(true)}
          onValueChange={(id: any) => {
            selectValue(id);
          }}>
          <Picker.Item
            fontFamily={
              I18nManager.isRTL
                ? theme.fonts.ar.semi_bold
                : theme.fonts.en.semi_bold
            }
            style={{fontWeight: 'bold'}}
            label={
              currentValue && isUnselectAllowed
                ? tr('picker.unSelect')
                : tr('picker.select')
            }
            value={''}
            enabled={isUnselectAllowed ? !!currentValue : enabled}
          />
          {list.map((item: string | PickerItem, i: number) => {
            return (
              <Picker.Item
                fontFamily={
                  I18nManager.isRTL
                    ? theme.fonts.ar.regular
                    : theme.fonts.en.regular
                }
                key={i}
                style={{fontWeight: 'bold'}}
                label={
                  labelKey && typeof item !== 'string'
                    ? item[labelKey]?.toString()
                    : item?.toString()
                }
                value={
                  valueKey && typeof item !== 'string' ? item[valueKey] : item
                }
              />
            );
          })}
        </AndroidPicker>
        <AndroidPressableContainer>
          <View onLayout={handleLayout}>
            <TouchableOpacity
              activeOpacity={0.7}
              style={[
                style,
                disabled && {
                  opacity: 0.5,
                },
              ]}
              disabled={disabled}
              onPress={() => pickerRef.current?.focus()}>
              {customComponent ? (
                customComponent()
              ) : (
                <TextInput
                  label={label}
                  value={getLabel()}
                  editable={false}
                  onChangeText={onValueChange}
                  showRightIcon
                  rightIconName={'arrowDown'}
                  isRequired={isRequired}
                  style={[{pointerEvents: 'none'}, style]}
                  {...props}
                />
              )}
            </TouchableOpacity>
          </View>
        </AndroidPressableContainer>
      </AndroidContainer>
    );
  };

  return Platform.OS === 'ios' ? renderIOS() : renderAndroid();
};

export default GenericPicker;
